import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  CircleAlert,
  Circle,
  Sparkles,
  Send,
  Loader2,
  RefreshCcw,
  AlertTriangle,
} from "lucide-react";
import { api } from "@/lib/api";

/**
 * AdminDailyMonthly — Sprint Devocional V3.1 · Item 4+5+6.
 * - Visão mensal (Jan, Fev, ..., Dez)
 * - Alerta de cobertura ≤7 dias
 * - Click no dia → preview com os 6 elementos
 * - Botões: Regenerar individual, Publicar dia, Enviar e-mail agora (dry-run + real)
 */

const MONTHS_PT = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];
const WEEKDAYS_SHORT = ["D", "S", "T", "Q", "Q", "S", "S"];

const STATUS_COLORS = {
  published: { bg: "bg-emerald-100", border: "border-emerald-300", text: "text-emerald-800", dot: "bg-emerald-500" },
  draft: { bg: "bg-amber-50", border: "border-amber-300", text: "text-amber-800", dot: "bg-amber-500" },
  missing: { bg: "bg-rose-50", border: "border-rose-300", text: "text-rose-700", dot: "bg-rose-500" },
  pending: { bg: "bg-[#FAF8F4]", border: "border-[#0B1E36]/15", text: "text-[#0B1E36]/55", dot: "bg-[#0B1E36]/25" },
};
const STATUS_LABEL = {
  published: "Publicado",
  draft: "Rascunho",
  missing: "Sem conteúdo",
  pending: "Pendente",
};

function isoOf(year, month, day) {
  return `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

export default function AdminDailyMonthly() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [monthData, setMonthData] = useState({}); // {iso: {status, sermon_title, theme_tag}}
  const [loading, setLoading] = useState(true);
  const [coverage, setCoverage] = useState(null);
  const [selectedIso, setSelectedIso] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const start = isoOf(year, month, 1);
    const endDate = new Date(year, month + 1, 0).getDate();
    const end = isoOf(year, month, endDate);
    api
      .get(`/admin/daily/calendar?start=${start}&end=${end}`)
      .then(({ data }) => {
        const map = {};
        (data.items || []).forEach((it) => {
          map[it.date] = it;
        });
        setMonthData(map);
      })
      .catch(() => setMonthData({}))
      .finally(() => setLoading(false));
    api.get("/admin/daily/coverage-alert").then(({ data }) => setCoverage(data)).catch(() => {});
  }, [year, month]);

  useEffect(() => {
    load();
  }, [load]);

  const days = useMemo(() => {
    const first = new Date(year, month, 1);
    const firstDow = first.getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstDow; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [year, month]);

  const goPrev = () => {
    if (month === 0) {
      setYear((y) => y - 1);
      setMonth(11);
    } else setMonth((m) => m - 1);
  };
  const goNext = () => {
    if (month === 11) {
      setYear((y) => y + 1);
      setMonth(0);
    } else setMonth((m) => m + 1);
  };

  const todayIso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

  const stats = useMemo(() => {
    const items = Object.values(monthData);
    return {
      published: items.filter((x) => x.status === "published").length,
      draft: items.filter((x) => x.status === "draft").length,
      pending: items.filter((x) => x.status === "pending").length,
      missing: Math.max(0, days.filter((d) => d !== null).length - items.length),
    };
  }, [monthData, days]);

  return (
    <section
      data-testid="admin-daily-monthly-section"
      className="mt-14 bg-white border border-[#0B1E36]/10 p-8"
    >
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="flex items-center gap-2 text-[#C5A059] mb-1">
            <Calendar size={12} strokeWidth={1.6} />
            <span className="text-[0.6rem] uppercase tracking-[0.28em] font-medium">
              Devocional, visão mensal
            </span>
          </div>
          <h2 className="font-serif-display text-[1.4rem] text-[#0B1E36] tracking-[-0.01em]">
            Pacotes diários do ano
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="admin-monthly-prev"
            onClick={goPrev}
            className="border border-[#0B1E36]/15 p-2 hover:bg-[#FAF8F4]"
          >
            <ChevronLeft size={14} />
          </button>
          <div className="font-serif-display text-[1.3rem] text-[#0B1E36] min-w-[200px] text-center">
            {MONTHS_PT[month]} <span className="text-[#0B1E36]/45 tabular-nums">{year}</span>
          </div>
          <button
            data-testid="admin-monthly-next"
            onClick={goNext}
            className="border border-[#0B1E36]/15 p-2 hover:bg-[#FAF8F4]"
          >
            <ChevronRight size={14} />
          </button>
        </div>
      </div>

      {/* Coverage alert */}
      {coverage && coverage.severity !== "ok" && (
        <div
          data-testid="admin-monthly-coverage-alert"
          className={`mb-5 px-4 py-3 border flex items-start gap-3 ${
            coverage.severity === "fail"
              ? "bg-rose-50 border-rose-300 text-rose-800"
              : "bg-amber-50 border-amber-300 text-amber-800"
          }`}
        >
          <AlertTriangle size={16} strokeWidth={1.7} className="mt-0.5 flex-shrink-0" />
          <div className="text-sm leading-snug">{coverage.message}</div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3 mb-5">
        <Stat label="Publicados" value={stats.published} color="emerald" />
        <Stat label="Rascunhos" value={stats.draft} color="amber" />
        <Stat label="Pendentes" value={stats.pending} color="slate" />
        <Stat label="Sem conteúdo" value={stats.missing} color="rose" />
      </div>

      {/* Calendar grid */}
      <div className="border border-[#0B1E36]/10">
        <div className="grid grid-cols-7 bg-[#FAF8F4] text-[0.55rem] uppercase tracking-[0.22em] text-[#0B1E36]/55 font-medium">
          {WEEKDAYS_SHORT.map((d, i) => (
            <div key={i} className="text-center py-2.5">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7" data-testid="admin-monthly-grid">
          {days.map((d, i) => {
            if (d === null) return <div key={i} className="bg-[#FAF8F4]/40 border-t border-l border-[#0B1E36]/8 aspect-square" />;
            const iso = isoOf(year, month, d);
            const item = monthData[iso];
            const status = item?.status || "missing";
            const c = STATUS_COLORS[status] || STATUS_COLORS.missing;
            const isToday = iso === todayIso;
            return (
              <button
                key={i}
                data-testid={`admin-monthly-day-${iso}`}
                onClick={() => setSelectedIso(iso)}
                className={`aspect-square ${c.bg} border-t border-l border-[#0B1E36]/8 p-2 text-left relative hover:ring-2 hover:ring-[#C5A059] transition-all ${isToday ? "ring-2 ring-[#0B1E36]" : ""}`}
              >
                <div className="flex items-start justify-between">
                  <span className={`text-[0.85rem] tabular-nums font-medium ${c.text}`}>{d}</span>
                  <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
                </div>
                {item?.sermon_title && (
                  <div className="mt-1 text-[0.58rem] text-[#0B1E36]/65 line-clamp-2 leading-tight">
                    {item.sermon_title}
                  </div>
                )}
                {item?.theme_tag && (
                  <div className="absolute bottom-1 left-2 text-[0.55rem] uppercase tracking-[0.16em] text-[#C5A059]">
                    {item.theme_tag}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {loading && (
        <div className="mt-3 text-center text-[#0B1E36]/45 text-sm">
          <Loader2 size={14} className="inline animate-spin mr-2" /> Carregando…
        </div>
      )}

      {/* Day detail drawer */}
      {selectedIso && (
        <DayDetailDrawer
          iso={selectedIso}
          onClose={() => setSelectedIso(null)}
          onChanged={load}
        />
      )}
    </section>
  );
}

function Stat({ label, value, color }) {
  const colors = {
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-800",
    amber: "border-amber-200 bg-amber-50 text-amber-800",
    rose: "border-rose-200 bg-rose-50 text-rose-700",
    slate: "border-[#0B1E36]/15 bg-[#FAF8F4] text-[#0B1E36]/65",
  };
  return (
    <div className={`border p-3 ${colors[color]}`}>
      <span className="block text-[0.55rem] uppercase tracking-[0.22em] mb-1">{label}</span>
      <p className="text-[1.5rem] tabular-nums font-light leading-none">{value}</p>
    </div>
  );
}

function DayDetailDrawer({ iso, onClose, onChanged }) {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // 'publish' | 'regen-field-{x}' | 'send-email'

  const load = useCallback(() => {
    setLoading(true);
    api
      .get(`/admin/daily/by-date/${iso}`)
      .then(({ data }) => setDoc(data))
      .catch(() => setDoc(null))
      .finally(() => setLoading(false));
  }, [iso]);

  useEffect(() => {
    load();
  }, [load]);

  const publish = async () => {
    setBusy("publish");
    try {
      await api.post(`/admin/daily/${iso}/publish`);
      onChanged();
      load();
    } finally {
      setBusy(null);
    }
  };

  const regenField = async (field) => {
    setBusy(`regen-${field}`);
    try {
      const { data } = await api.post(`/admin/daily/${iso}/regenerate-field?field=${field}`);
      setDoc(data);
      onChanged();
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Falha ao regenerar.");
    } finally {
      setBusy(null);
    }
  };

  const sendEmail = async (dryRun) => {
    setBusy("send-email");
    try {
      const { data } = await api.post(`/admin/daily/send-now?dry_run=${dryRun}`);
      window.alert(
        dryRun
          ? `Dry-run: ${data.sent} destinatários seriam alcançados.`
          : `Enviado para ${data.sent} destinatários. Falhas: ${data.failed}.`
      );
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Falha no envio.");
    } finally {
      setBusy(null);
    }
  };

  const realignDay = async () => {
    setBusy("realign");
    try {
      // Preview
      const { data: preview } = await api.post(`/admin/daily/${iso}/realign?dry_run=true`);
      const ok = window.confirm(
        `Realinhar ${iso} ao tema semanal?\n\n` +
        `Atual: [${doc?.sermon_category}] ${doc?.sermon_title}\n` +
        `Novo:  [${preview.proposed_sermon.category}] ${preview.proposed_sermon.title}\n\n` +
        `Score projetado: ${preview.projected_alignment.score} (${preview.projected_alignment.level})`
      );
      if (!ok) return;
      const { data } = await api.post(`/admin/daily/${iso}/realign`);
      onChanged?.(data);
      window.alert("Dia realinhado. Devocional regenerado com sermão do tema semanal.");
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Falha ao realinhar.");
    } finally {
      setBusy(null);
    }
  };

  const realignWeek = async () => {
    const ws = doc?.weekly_theme?.week_start;
    if (!ws) {
      window.alert("Nenhum tema semanal ativo neste dia.");
      return;
    }
    setBusy("realign-week");
    try {
      const { data: preview } = await api.get(`/admin/realign/week?week_start=${ws}&min_score=50`);
      const realign = (preview.plan || []).filter((s) => s.action === "realign");
      if (realign.length === 0) {
        window.alert("Todos os dias da semana já estão suficientemente alinhados (score ≥ 50).");
        return;
      }
      const msg =
        `Realinhar ${realign.length} dia(s) da ${preview.weekly_theme?.theme_name}?\n\n` +
        realign
          .map((s) => `${s.date}: ${s.current_score} → ${s.projected_score}\n  ${s.current_title?.slice(0, 40)} → ${s.proposed_title?.slice(0, 40)}`)
          .join("\n\n");
      if (!window.confirm(msg)) return;
      const { data } = await api.post(`/admin/realign/week?week_start=${ws}&dry_run=false&min_score=50`);
      window.alert(`${data.realigned_count} dia(s) realinhados: ${data.realigned_dates.join(", ")}`);
      // recarregar dia atual
      const { data: fresh } = await api.get(`/admin/daily/by-date/${iso}`);
      onChanged?.(fresh);
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Falha ao realinhar semana.");
    } finally {
      setBusy(null);
    }
  };

  if (!doc && !loading) return null;

  return (
    <div
      className="fixed inset-0 z-[90] bg-[#0B1E36]/45 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        data-testid="admin-monthly-day-drawer"
        className="bg-white max-w-3xl w-full my-12 border border-[#0B1E36]/15 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b border-[#0B1E36]/10 flex items-center justify-between p-5 z-10">
          <div>
            <p className="text-[0.6rem] uppercase tracking-[0.3em] text-[#C5A059] font-medium">
              {new Date(iso + "T00:00:00").toLocaleDateString("pt-BR", { dateStyle: "full" })}
            </p>
            <h2 className="font-serif-display text-[1.4rem] text-[#0B1E36]">
              {doc?.devotional?.title || "Devocional"}
            </h2>
          </div>
          <button onClick={onClose} className="text-[#0B1E36]/65 hover:text-[#0B1E36] text-xl">✕</button>
        </div>

        {loading ? (
          <div className="p-10 text-center text-[#0B1E36]/45">
            <Loader2 size={18} className="inline animate-spin mr-2" /> Carregando…
          </div>
        ) : doc ? (
          <div className="p-6 space-y-5">
            <Pill status={doc.status} edited={doc.edited} source={doc.source} />

            {doc.alignment_score && doc.alignment_score.score !== null && (
              <div
                data-testid="day-alignment-score"
                className={`flex items-center justify-between gap-3 p-3 rounded-lg border ${
                  doc.alignment_score.ui?.color === "emerald" ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                  : doc.alignment_score.ui?.color === "sky" ? "bg-sky-50 border-sky-200 text-sky-800"
                  : doc.alignment_score.ui?.color === "amber" ? "bg-amber-50 border-amber-200 text-amber-800"
                  : doc.alignment_score.ui?.color === "rose" ? "bg-rose-50 border-rose-200 text-rose-800"
                  : "bg-slate-50 border-slate-200 text-slate-700"
                }`}
              >
                <div className="text-sm">
                  <span className="font-semibold">Aderência ao tema:</span>{" "}
                  {doc.alignment_score.score}/100 · {doc.alignment_score.ui?.label}
                  {doc.weekly_theme && (
                    <span className="ml-2 text-xs opacity-75">
                      tema: {doc.weekly_theme.theme_name} ({doc.weekly_theme.anchor_category})
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  {doc.alignment_score.ui?.show_realign && (
                    <button
                      type="button"
                      data-testid="day-realign"
                      disabled={busy === "realign"}
                      onClick={realignDay}
                      className="text-[0.7rem] uppercase tracking-wider px-3 py-1.5 rounded bg-[#0B1E36] text-white hover:bg-[#122a4c] disabled:opacity-60"
                    >
                      {busy === "realign" ? "..." : "Realinhar dia"}
                    </button>
                  )}
                  {doc.weekly_theme && (
                    <button
                      type="button"
                      data-testid="day-realign-week"
                      disabled={busy === "realign-week"}
                      onClick={realignWeek}
                      className="text-[0.7rem] uppercase tracking-wider px-3 py-1.5 rounded border border-[#0B1E36]/25 text-[#0B1E36] hover:bg-[#0B1E36]/5 disabled:opacity-60"
                    >
                      {busy === "realign-week" ? "..." : "Realinhar semana"}
                    </button>
                  )}
                </div>
              </div>
            )}

            <Block label="Sermão base">
              <p>{doc.sermon_title} <span className="text-[#0B1E36]/45">, {doc.sermon_category}</span></p>
            </Block>

            <Block label="Versículo central">
              <p className="font-serif-display italic text-[1.05rem]">&ldquo;{doc.central_verse?.text}&rdquo;</p>
              <p className="mt-2 text-[0.75rem] uppercase tracking-[0.22em] text-[#C5A059]">
                {doc.central_verse?.reference}
              </p>
            </Block>

            <Block
              label="Introdução"
              testid="day-introduction"
              action={<RegenBtn busy={busy} field="introduction" onClick={regenField} />}
            >
              <p className="whitespace-pre-wrap text-[0.95rem] leading-relaxed">
                {doc.devotional?.introduction}
              </p>
            </Block>

            <Block label="Resumo" action={<RegenBtn busy={busy} field="summary" onClick={regenField} />}>
              <p className="text-[0.95rem] leading-relaxed">{doc.devotional?.summary}</p>
            </Block>

            <Block label="Frase de destaque" action={<RegenBtn busy={busy} field="highlight" onClick={regenField} />}>
              <p
                className="font-serif-display italic text-[1.1rem] border-l-2 border-[#C5A059] pl-4"
                dangerouslySetInnerHTML={{
                  __html: (doc.devotional?.highlight_phrase || "").replace(/\*\*([^*]+)\*\*/g, '<strong class="text-[#C5A059]">$1</strong>'),
                }}
              />
              {doc.devotional?.keywords?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {doc.devotional.keywords.filter(Boolean).map((k) => (
                    <span key={k} className="text-[0.62rem] uppercase tracking-[0.18em] text-[#C5A059] bg-[#C5A059]/10 px-2 py-0.5">
                      {k}
                    </span>
                  ))}
                </div>
              )}
            </Block>

            <Block label="Conclusão" action={<RegenBtn busy={busy} field="conclusion" onClick={regenField} />}>
              <p className="text-[0.95rem] leading-relaxed">{doc.devotional?.conclusion}</p>
            </Block>

            <Block label="Frase do dia" action={<RegenBtn busy={busy} field="phrase" onClick={regenField} />}>
              <p className="text-[0.95rem] italic">{doc.phrase}</p>
            </Block>

            <Block label="Oração do dia" action={<RegenBtn busy={busy} field="prayer" onClick={regenField} />}>
              <p className="text-[0.95rem] leading-relaxed whitespace-pre-wrap">{doc.prayer}</p>
            </Block>

            <Block label="Capítulos complementares" action={<RegenBtn busy={busy} field="related_chapters" onClick={regenField} />}>
              <ul className="space-y-1.5 text-[0.92rem]">
                {(doc.related_chapters || []).map((r, i) => (
                  <li key={i}>
                    <strong className="text-[#0B1E36]">{r.reference}</strong>
                    <span className="text-[#0B1E36]/65">, {r.why}</span>
                  </li>
                ))}
              </ul>
            </Block>

            <div className="border-t border-[#0B1E36]/10 pt-5 flex flex-wrap gap-2">
              {doc.status === "draft" && (
                <button
                  data-testid="day-publish-btn"
                  disabled={busy === "publish"}
                  onClick={publish}
                  className="inline-flex items-center gap-2 text-[0.7rem] uppercase tracking-[0.22em] bg-emerald-700 hover:bg-emerald-800 text-white font-medium px-4 py-2.5"
                >
                  <CheckCircle2 size={13} strokeWidth={1.7} />
                  Publicar este dia
                </button>
              )}
              <button
                disabled={busy === "regen-all"}
                onClick={() => regenField("all")}
                className="inline-flex items-center gap-2 text-[0.7rem] uppercase tracking-[0.22em] border border-[#0B1E36]/15 text-[#0B1E36] hover:bg-[#FAF8F4] font-medium px-4 py-2.5"
              >
                <RefreshCcw size={13} strokeWidth={1.7} />
                Regerar tudo
              </button>
              <button
                data-testid="day-send-email-dry"
                disabled={busy === "send-email"}
                onClick={() => sendEmail(true)}
                className="inline-flex items-center gap-2 text-[0.7rem] uppercase tracking-[0.22em] border border-[#C5A059] text-[#C5A059] hover:bg-[#C5A059]/10 font-medium px-4 py-2.5"
              >
                <Send size={13} strokeWidth={1.7} />
                Simular envio
              </button>
              <button
                data-testid="day-send-email-real"
                disabled={busy === "send-email"}
                onClick={() => {
                  if (window.confirm("Enviar e-mail real para todos os subscribers?")) sendEmail(false);
                }}
                className="inline-flex items-center gap-2 text-[0.7rem] uppercase tracking-[0.22em] bg-[#C5A059] hover:bg-[#D4B273] text-[#0B1E36] font-medium px-4 py-2.5"
              >
                <Send size={13} strokeWidth={1.7} />
                Enviar agora
              </button>
            </div>
          </div>
        ) : (
          <div className="p-10 text-center text-[#0B1E36]/45">Nada por aqui.</div>
        )}
      </div>
    </div>
  );
}

function Block({ label, children, action, testid }) {
  return (
    <div data-testid={testid} className="border-b border-[#0B1E36]/8 pb-4">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[0.6rem] uppercase tracking-[0.28em] text-[#C5A059] font-medium">{label}</p>
        {action}
      </div>
      <div className="text-[#0B1E36]">{children}</div>
    </div>
  );
}

function RegenBtn({ busy, field, onClick }) {
  const isThis = busy === `regen-${field}`;
  return (
    <button
      data-testid={`day-regen-${field}`}
      disabled={!!busy}
      onClick={() => onClick(field)}
      className="text-[0.6rem] uppercase tracking-[0.18em] text-[#0B1E36]/55 hover:text-[#C5A059] inline-flex items-center gap-1"
    >
      {isThis ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} strokeWidth={1.7} />}
      Regerar
    </button>
  );
}

function Pill({ status, edited, source }) {
  const s = STATUS_COLORS[status] || STATUS_COLORS.draft;
  return (
    <div className="flex items-center gap-2">
      <span className={`inline-flex items-center gap-1.5 ${s.bg} ${s.border} ${s.text} border px-2.5 py-1 text-[0.6rem] uppercase tracking-[0.22em] font-medium`}>
        <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
        {STATUS_LABEL[status]}
      </span>
      {edited && (
        <span className="text-[0.55rem] uppercase tracking-[0.22em] text-[#0B1E36]/65 bg-[#0B1E36]/8 px-2 py-1">
          editado
        </span>
      )}
      <span className="text-[0.55rem] uppercase tracking-[0.22em] text-[#0B1E36]/45">
        fonte, {source}
      </span>
    </div>
  );
}
